import {_} from 'lodash';
import PromiseRequest from "../../req/PromiseRequest";
import {
    ICondition,
    IKnockoutComponent,
    IObjectType,
    IRouteDefinition,
    IRule,
    IRuleset,
    TCondition
} from "../../index";
import {IFullObjectType} from "./index";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../constants";
import {getObjectDefinition, getObjectType} from "../../util/misc";
import {getPrettyDate, validateServerDate} from "../../util/datetime";
import {global, lm} from "../../appContext";
import {nsClone} from "../../clone/fn";
import {nsRequest} from "../../req/fn";
import {TComponentName} from "../../constants/ComponentRegistry";
import pubsub from "../../pubsub";
import {ICloneDialogOptions} from "../../clone/index";
import {IRequestObject} from "../../req/index";
import {ImportFormatId} from "../../constants/ImportFormats";
import getPromiseRequest = nsRequest.getPromiseRequest;

export namespace nsRuleset {

    export const AUTH_DATA_SOURCES_DELIMITER = ',';

    export const MAX_DESCRIPTION_LENGTH = 1000

    export function invalidateCachesAfterRulesetMutation(): void {
        global.cacheManager.removeCache('getruleset');
        global.cacheManager.removeCache('searchruleset');
        global.cacheManager.removeCache('getehubprocess');
        global.cacheManager.removeCache('getexporttemplate');
        global.cacheManager.removeCache('searchexporttemplate');
        global.cacheManager.removeCache('getmessage');
        global.cacheManager.removeCache('searchmessage');
    }

    export const RULE_DEFAULT: IRule = {
        argument: '',
        argumentoperator: '' as TCondition,
        attribute: '',
        booleanoperator: '',
        id: -1,
        order: 0,
        rulesetid: 0,
        rulesetpath: '',
    }

    export const COMPONENT_NAMES: Record<IObjectType['name'], TComponentName> = {
        'User': 'component/ruleset/preview/user',
        'Group': 'component/ruleset/preview/group',
        'Department': 'component/ruleset/preview/department',
        'FormData': 'component/ruleset/preview/formdata',
        'ClientCredential': 'component/ruleset/preview/clientcredential',
    };

    export function fnPreviewRuleset(ruleset: IRuleset) {
        global.dialogManager.addCustom(getPreviewComponent(ruleset))
    }

    export function fnPreviewRulesetMutable(rulesetNew: IRuleset, rulesetOld: IRuleset) {
        const rsn = _.cloneDeep(rulesetNew);
        const rso = _.cloneDeep(rulesetOld);
        rsn.rules = _.sortBy(rsn.rules, "order");
        rso.rules = _.sortBy(rso.rules, "order");

        global.dialogManager.addCustom({
            name: COMPONENT_NAMES[rsn.objecttype.name]!,
            params: {
                ruleset: rsn,
                rulesetOld: rso,
            }
        })
    }

    function getPreviewComponent(ruleset: IRuleset): IKnockoutComponent {
        const name = COMPONENT_NAMES[ruleset.objecttype.name]!;
        const __routeDef = {
            icon: 'fa-eye',
            id: null!,
            labelLong: 'preview',
            level: 1,
            showHeadingButtons: false
        }

        const params: IKnockoutComponent['params'] = _.defaultsDeep({ruleset, isClosable: true}, {__routeDef}, {__dialog: {width: 'auto'}});
        return {name, params}
    }

    export function create(src: IRuleset, trgOrgnr: string, params: any = {refs: {}}): Promise<IRuleset[]> {
        const srcOrgnr = src.companyid;
        const isSrcEqualTrg = srcOrgnr == trgOrgnr;
        const cpy: IRuleset = _.defaultsDeep({companyid: trgOrgnr}, params, _.cloneDeep(src));
        cpy.id = -1;
        cpy.companyid = trgOrgnr;

        if (isSrcEqualTrg) {
            cpy.name += ` (${lm.t('copy (duplicate)|ac')})`;
        }

        return Promise.resolve()
            .then(() => {
                // --- COPY SUB RULESETS -------------------------------------------------------------------------------
                if (!isSrcEqualTrg) {
                    return Promise.all(cpy.rules.map(r => {
                        switch (r.argumentoperator) {
                            case 'Omfattes av':
                            case 'Omfattes ikke av':
                                return getPromiseRequest('getruleset')
                                    .request({
                                        id: r.argument,
                                        orgnr: src.companyid,
                                    })
                                    .then((data: IRuleset[]) => {
                                        const subRuleset = data[0];
                                        if (!subRuleset) {
                                            return r;
                                        }
                                        return create(subRuleset, trgOrgnr).then(created => {
                                            const createdRuleset = created[0];
                                            if (!createdRuleset) {
                                                return r;
                                            }
                                            r.argument = createdRuleset.id;
                                            return r;
                                        });
                                    })
                            default:
                                return Promise.resolve(r);
                        }
                    }))
                }
            })
            .then(() => {
                // --- COPY RULESET ------------------------------------------------------------------------------------
                return new PromiseRequest("createruleset")
                    .request({
                        jruleset: JSON.stringify(cpy),
                        orgnr: trgOrgnr
                    })
                    .then((data: IRuleset[]) => {
                        invalidateCachesAfterRulesetMutation();
                        return data;
                    });
            })
    }

    export function getById(id: number): IRequestObject {
        const promiseRequest = global.getPromiseRequest("getruleset")
        return {
            promise: promiseRequest,
            request: promiseRequest.request({id}).then((data: [IRuleset]) => data[0]),
        }
    }

    export function find(name: string, orgnr: string): Promise<IRuleset[]> {
        return new PromiseRequest("searchruleset")
            .request({
                orgnr: orgnr,
                asc: true,
                pagenr: 0,
                resultsprpage: MAX_RESULTS_PER_PAGE,
                searchkey: name,
                sortcol: "name"
            })
            .then((data: IRuleset[]) => {
                const item = data.find((item: IRuleset) => item.name === name);
                return item ? [item] : [];
            });
    }

    export function find_reqObj(name: string, orgnr: string): IRequestObject {
        const promise = new PromiseRequest("searchruleset")
        const request = promise
            .request({
                orgnr: orgnr,
                asc: true,
                pagenr: 0,
                resultsprpage: MAX_RESULTS_PER_PAGE,
                searchkey: name,
                sortcol: "name"
            })
            .then((data: IRuleset[]) => {
                const item = data.find((item: IRuleset) => item.name === name);
                return item ? [item] : [];
            });
        return {promise, request}
    }

    export function trash(ruleset: IRuleset): Promise<any> {
        return new PromiseRequest("deleteruleset")
            .request({id: ruleset.id})
            .then(() => {
                invalidateCachesAfterRulesetMutation();
                pubsub.trigger('delete.ruleset.complete');
            });
    }

    const DialogOptions: ICloneDialogOptions = {icon: 'fa-ruleset', translationKey: 'ruleset'};

    export function copyDialog(ruleset: IRuleset) {
        nsClone.dialogCopy(DialogOptions, "component/ruleset/copy-wizard/main", ruleset)
    }

    export function trashDialog(ruleset: IRuleset) {
        nsClone.dialogDelete(DialogOptions, "component/ruleset/dialog/delete", ruleset)
    }

    export function getAll(): Promise<IRuleset[]> {
        return global.getPromiseRequest("searchruleset")
            .request({
                searchkey: "",
                resultsprpage: MAX_RESULTS_PER_PAGE,
                pagenr: 0,
                orgnr: global.login.selectedOrganizationId(),
                sortcol: "name",
                asc: true,
            });
    }

    export function getConditions(): IRequestObject {
        const promise = global.getPromiseRequest("getconditions");
        const request = promise.request();
        return {promise, request}
    }

    export function getCondition(argumentoperator: string, conditions: ICondition[]): ICondition | undefined {
        return conditions.find((item: ICondition) => item.name == argumentoperator);
    }

    export function validateRange(str: string): boolean {
        return /^\[NOW[+-]?\d*]$/gi.test(str);
    }

    export function getRangeValue(str: string): number | null {
        if (!validateRange(str))
            return null;

        const ret = /^\[NOW([+-]\d*)]$/gi.exec(str);
        if (!ret || ret.length < 2) {
            return 0;
        }
        return parseInt(ret[1] ?? '0')
    }

    export function getDateRangeHumanReadable(str: string | null): string | null {
        if (str === null) {
            return null;
        }
        if (validateRange(str)) {
            const val = getRangeValue(str);
            if (val === null) {
                return null;
            }
            let out: string = (val >= 0 ? '+' : '') + val.toString();
            return lm.t(`((Now)) ${out} ((day|p))`);
        }
        if (validateServerDate(str)) {
            return getPrettyDate(str, true);
        }
        return str
    }

    /**
     * Traverses the whole ruleset graph (ie. rules that include or exclude other rulesets) to find a rule that is
     * concerned with deleted (attribute == "Deleted").
     *
     * A nested ruleset is considered to include or exclude based on argumentoperator being respectively "Omfattes av"
     * or "Omfattes ikke av".
     *
     * If ruleset is included then the algorithm will search for argumentoperator != "Har ikke verdi".
     * If ruleset is excluded then the algorithm will search for argumentoperator == "Har ikke verdi".
     */
    export function isRulesetNestedDelete(ruleset: IRuleset, allRulesets: IRuleset[], includes: boolean = true): boolean {
        const ruleConcernedWithDeleted = ruleset.rules.find(r => r.attribute == "Deleted");
        if (ruleConcernedWithDeleted) {
            return includes
                ? ruleConcernedWithDeleted.argumentoperator != 'Har ikke verdi'
                : ruleConcernedWithDeleted.argumentoperator == 'Har ikke verdi'
        }
        return !!ruleset.rules.find((r: IRule): boolean => {
            const argumentoperator = r.argumentoperator;
            if (argumentoperator == 'Omfattes av' || argumentoperator == 'Omfattes ikke av') {
                const _includes = argumentoperator == 'Omfattes av';
                const _ruleset = allRulesets.find(rs => rs.id == r.argument);
                if (!_ruleset) {
                    return false;
                }
                return isRulesetNestedDelete(_ruleset, allRulesets, _includes);
            }
            return false;
        });
    }

    function isRulesetDelete(ruleset: IRuleset): boolean {
        return !!ruleset.rules.find(r => r.attribute == "Deleted" && r.argumentoperator != "Har ikke verdi");
    }

    function isRulesetNested(ruleset: IRuleset): boolean {
        return !!ruleset.rules.find(rule => {
            const operator = rule.argumentoperator;
            return operator == 'Omfattes av' || operator == 'Omfattes ikke av';
        })
    }

    export function generateRule(rule: Partial<IRule>): IRule {
        return _.defaults(rule, RULE_DEFAULT);
    }

    export function generateRuleset(ruleset: Partial<IRuleset> = {}): IRuleset {
        const defaultRuleset: IRuleset = {
            authdatasources: '',
            companyid: global.login.selectedOrganizationId() ?? '',
            departmentexpression: '',
            description: '',
            evaluatepositions: false,
            id: 0,
            name: '',
            objecttype: getObjectType(ObjectTypeId.User),
            refs: {},
            rules: [],
        };
        return _.defaultsDeep(ruleset, defaultRuleset);
    }

    export function getFullObjectType(objectTypeId: ObjectTypeId): IFullObjectType {
        const def = getObjectDefinition(objectTypeId);
        return {
            def: def!,
            id: objectTypeId,
            type: getObjectType(objectTypeId),
        }
    }

    export function getRulesForFetchingAManager(argument: IRule["argument"], importFormatId?: ImportFormatId): IRule[] {
        const formatId = importFormatId ?? global.login.selectedOrganization()?.importformat?.id;
        let rules: Partial<IRule>[];

        switch (formatId) {
            case ImportFormatId.Visma:
            case ImportFormatId.Visma_Hierachy: {
                rules = [{
                    argument: argument,
                    attribute: 'UserId',
                    argumentoperator: 'Er lik',
                    booleanoperator: 'Or',
                    order: 1,
                }, {
                    argument: argument,
                    attribute: 'AUserId',
                    argumentoperator: 'Er lik en av',
                    booleanoperator: 'Or',
                    order: 2,
                }]
                break
            }

            case ImportFormatId.Personec_P_HR:
            case ImportFormatId.Personec_P_HI:
            case ImportFormatId.Visma_Employee_HR:
            case ImportFormatId.Visma_Employee_HI: {
                rules = [{
                    argument: argument,
                    attribute: 'SourceId',
                    argumentoperator: 'Er lik',
                }]
                break
            }

            default: {
                rules = [{
                    argument: argument,
                    attribute: 'AEmployeeNumber',
                    argumentoperator: 'Er lik en av',
                    order: 1,
                }, {
                    argument: argument,
                    attribute: 'EmployeeNumber',
                    argumentoperator: 'Er lik',
                    booleanoperator: 'Or',
                    order: 2,
                }]
            }

        }
        return rules.map(r => generateRule(r));
    }

    export function getRefsObjectWithoutReferenceToSubRulesets(ruleset: IRuleset): IRuleset['refs'] {
        const ret = _.cloneDeep(ruleset.refs)
        const rulesetRefs = getRefsWithoutReferenceToSubRulesets(ruleset) ?? []
        if (!rulesetRefs.length) {
            delete ret.jruleset
        } else {
            ret.jruleset = rulesetRefs
        }
        return ret
    }

    export function getRefsWithoutReferenceToSubRulesets(ruleset: IRuleset): IRuleset['refs']['jruleset'] {
        return ruleset.refs?.jruleset?.filter(id => {
            const sourceRulesetUsesTargetRulesetId = ruleset.rules.find(rule => ['Omfattes av', 'Omfattes ikke av'].includes(rule.argumentoperator) && rule.argument == id)
            return !sourceRulesetUsesTargetRulesetId
        }) ?? []
    }

}
